import Debug from 'debug';
import { useEffect, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { ConditionElement, Entity, Filter, getConditionIdentityKey, QueryOptions, Row, Sort } from 'gossamer-unified';
import { setSearchQueryOptions } from '../ancillary/ListManager';
import { checkQueryOptionsChanges, DEFAULT_PAGE_SIZE, initializeQueryOptions } from './Utils';

const debug = Debug('gfe/da/useQueryOptionsFromSearchParams');

const parseQueryOptionsFromSearchParams = (searchParams: URLSearchParams): QueryOptions | undefined => {
  try {
    const raw = searchParams.get('q');
    if (!raw) {
      return undefined;
    }
    return JSON.parse(raw);
  } catch (e) {
    return undefined;
  }
};

const ensureLimit = (options: QueryOptions): void => {
  if (typeof options.limit !== 'number') {
    options.limit = DEFAULT_PAGE_SIZE;
  }
};

const sanitizeOrder = (entity: Entity<any>, options: QueryOptions, initialOptions?: QueryOptions): void => {
  const rawOrder = Array.isArray(options.order) ? (options.order as Sort[]) : [];
  const sanitizedOrder = rawOrder.filter((s) => {
    const fieldId = s?.[0];
    return typeof fieldId === 'string' && !!entity.fields?.[fieldId];
  });

  if (sanitizedOrder.length > 0) {
    options.order = sanitizedOrder;
    return;
  }

  options.order = (initialOptions?.order as Sort[] | undefined) || (entity.defaultSort as Sort[] | undefined) || [];
};

const sanitizeWhereAnd = (entity: Entity<any>, options: QueryOptions): ConditionElement[] => {
  const rawWhereAnd = Array.isArray(options.where?.and) ? (options.where.and as ConditionElement[]) : [];
  return rawWhereAnd.filter((c) => {
    const filter = c as Filter;
    const fieldId = filter?.fieldId;
    if (!fieldId) {
      // Keep non-filter ConditionElements; UI will ignore ones without a fieldId.
      return true;
    }
    return typeof fieldId === 'string' && !!entity.fields?.[fieldId];
  });
};

const getBaseWhereAnd = (entity: Entity<any>, initialOptions?: QueryOptions): ConditionElement[] => {
  return (initialOptions?.where as any)?.and || (entity.defaultFilters as ConditionElement[] | undefined) || [];
};

const getHiddenBaseFilters = (entity: Entity<any>, baseWhereAnd: ConditionElement[]): ConditionElement[] => {
  return baseWhereAnd.filter((c) => {
    const filter = c as Filter;
    const fieldId = filter?.fieldId;
    return filter?.display === 'hidden' && typeof fieldId === 'string' && !!entity.fields?.[fieldId];
  });
};

const ensureWhere = (options: QueryOptions): void => {
  if (!options.where) {
    options.where = { and: [] };
  }
};

const mergeHiddenBaseFilters = (outAnd: ConditionElement[], hiddenBaseFilters: ConditionElement[]): void => {
  if (hiddenBaseFilters.length === 0) {
    return;
  }

  // A hidden base filter should be considered "present" if any filter with the same uniqueId/fieldId+operator
  // already exists (even if some other hook later changes its display from 'hidden' to 'value-edit', etc).
  // Otherwise we'd keep prepending the hidden version on every render.
  const existingKeys = new Set(
    outAnd.map((c) => getConditionIdentityKey(c)).filter((k): k is string => typeof k === 'string' && k.length > 0),
  );

  // Prepend (not append) so callers relying on hidden filter ordering (e.g. institution_id first) keep working.
  [...hiddenBaseFilters].reverse().forEach((c) => {
    const key = getConditionIdentityKey(c);
    if (!key) {
      return;
    }
    if (!existingKeys.has(key)) {
      outAnd.unshift(c);
      existingKeys.add(key);
    }
  });
};

export const getQueryOptionsFromSearchParams = (
  entity: Entity<any>,
  searchParams: URLSearchParams,
  initialOptions?: QueryOptions,
) => {
  const optionsFromUrl = parseQueryOptionsFromSearchParams(searchParams);

  // Start with URL options if present, otherwise use the provided initial options.
  // We'll also validate/sanitize below so stale query strings from other entity pages don't break this one.
  const options: QueryOptions = (optionsFromUrl || initialOptions || {}) as QueryOptions;

  ensureLimit(options);
  sanitizeOrder(entity, options, initialOptions);

  const sanitizedWhereAnd = sanitizeWhereAnd(entity, options);
  const baseWhereAnd = getBaseWhereAnd(entity, initialOptions);
  const hiddenBaseFilters = getHiddenBaseFilters(entity, baseWhereAnd);

  ensureWhere(options);
  const where = options.where!;
  where.and = sanitizedWhereAnd;

  console.log('getQueryOptionsFromSearchParams', { options, sanitizedWhereAnd, baseWhereAnd, hiddenBaseFilters });

  // If URL contains no filters, fall back to initial/entity defaults.
  if (!Array.isArray(where.and) || where.and.length === 0) {
    where.and = baseWhereAnd;
  } else if (hiddenBaseFilters.length > 0) {
    // Always include hidden filters even if not present in the URL.
    const outAnd = where.and;
    mergeHiddenBaseFilters(outAnd, hiddenBaseFilters);
  }

  console.log('getQueryOptionsFromSearchParams', { options });

  return options;
};

export const getSearchParamsFromQueryOptions = (oldSearchParams: URLSearchParams, queryOptions: QueryOptions) => {
  const newSearchParams = new URLSearchParams(oldSearchParams);
  newSearchParams.set('q', JSON.stringify(queryOptions || {}));
  return newSearchParams;
};

export const useQueryOptionsChanges = <T extends Row>(entity: Entity<T>, extQueryOptions?: QueryOptions) => {
  const [prevPropQueryOptions, setPrevPropQueryOptions] = useState<QueryOptions>(extQueryOptions || {});
  const [currQueryOptions, setCurrQueryOptions] = useState<QueryOptions>(extQueryOptions || {});
  useEffect(() => {
    const nextPropQueryOptions = extQueryOptions || {};
    if (!checkQueryOptionsChanges(nextPropQueryOptions, prevPropQueryOptions)) {
      return;
    }
    debug(`propsQueryOptions change`);
    setPrevPropQueryOptions(nextPropQueryOptions);
    setCurrQueryOptions(initializeQueryOptions(entity, nextPropQueryOptions));
  }, [extQueryOptions]);
  return [currQueryOptions, setCurrQueryOptions] as [QueryOptions, (opts: QueryOptions) => void];
};

export const useQueryOptionsFromSearchParams = <T extends Row>(
  entity: Entity<T>,
  initializeQueryOptions?: QueryOptions,
) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { pathname } = useLocation();
  const queryOptions = getQueryOptionsFromSearchParams(entity, searchParams, initializeQueryOptions);
  setSearchQueryOptions(pathname, queryOptions);
  const setQueryOptions = (newQueryOptions: QueryOptions) => {
    debug(`setQueryOptions`);
    checkQueryOptionsChanges(newQueryOptions, queryOptions);
    setSearchParams(getSearchParamsFromQueryOptions(searchParams, newQueryOptions));
  };
  return [queryOptions, setQueryOptions] as [QueryOptions, (args: QueryOptions) => void];
};
